import { useSyncExternalStoreWithSelector } from "use-sync-external-store/with-selector.js";

import { type ReadonlyStore } from "./createStore";

const identity = <T, S>(state: T): S => state as unknown as S;

/**
 * Shallow equality for `useStore`'s `isEqual`. Use it when a selector returns a fresh object/array each call, so the component re-renders only when a selected value actually changes.
 */
export function shallow<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || a === null || typeof b !== "object" || b === null) return false;
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) =>
      Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
    )
  );
}

/**
 * Subscribe a component to a store, re-rendering only when the selected slice changes.
 * Pass `isEqual` when the selector returns a fresh object/array. See ./README.md.
 */
export function useStore<T, S = T>(
  store: ReadonlyStore<T>,
  selector: (state: T) => S = identity,
  isEqual: (a: S, b: S) => boolean = Object.is,
): S {
  return useSyncExternalStoreWithSelector(store.subscribe, store.get, store.get, selector, isEqual);
}
